import { useMemo, useState } from 'react';
import { Search, Truck, ChevronDown } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { VEHICLE_SPECS } from '@/data/vehicles';
import type { Station, Vehicle, VehicleStatus } from '@/state/types';

const STATUS_COLOR: Record<VehicleStatus, string> = {
  1: 'bg-status-available text-bg-primary',
  2: 'bg-status-available text-bg-primary',
  3: 'bg-status-enroute text-white',
  4: 'bg-status-onscene text-white',
  5: 'bg-accent-dispatch text-white',
  6: 'bg-status-offduty text-white',
  7: 'bg-status-transport text-white',
  8: 'bg-status-transport text-white',
};

export function VehiclesTab() {
  const stations = useGameStore((s) => s.stations);
  const vehicles = useGameStore((s) => s.vehicles);
  const selectDetail = useGameStore((s) => s.selectDetail);

  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const grouped = useMemo(() => {
    return stations
      .map((st) => {
        const matched = vehicles
          .filter((v) => v.stationId === st.id)
          .filter((v) => {
            if (!q) return true;
            const spec = VEHICLE_SPECS[v.type];
            return (
              spec.label.toLowerCase().includes(q) ||
              spec.fullName.toLowerCase().includes(q) ||
              st.name.toLowerCase().includes(q) ||
              v.id.toLowerCase().includes(q)
            );
          });
        return { station: st, vehicles: matched };
      })
      .filter((g) => g.vehicles.length > 0);
  }, [stations, vehicles, q]);

  const total = vehicles.length;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border-subtle px-5 py-3">
        <h2 className="font-display text-base font-bold">Fahrzeuge ({total})</h2>
        <div className="mt-3 flex items-center gap-2 rounded-md border border-border-subtle bg-bg-elevated px-3 py-2">
          <Search className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Fahrzeug oder Wache…"
            className="w-full bg-transparent text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none"
          />
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        {total === 0 ? (
          <EmptyState />
        ) : grouped.length === 0 ? (
          <p className="px-2 py-8 text-center text-xs text-fg-muted">
            Keine Treffer für „{query}".
          </p>
        ) : (
          <ul className="space-y-3">
            {grouped.map(({ station, vehicles: vs }) => (
              <Group
                key={station.id}
                station={station}
                vehicles={vs}
                onSelectVehicle={(v) => selectDetail({ kind: 'vehicle', id: v.id })}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface GroupProps {
  station: Station;
  vehicles: Vehicle[];
  onSelectVehicle: (v: Vehicle) => void;
}

function Group({ station, vehicles, onSelectVehicle }: GroupProps) {
  const [expanded, setExpanded] = useState(true);
  return (
    <li className="overflow-hidden rounded-lg border border-border-subtle bg-bg-elevated/30">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition hover:bg-bg-elevated/60"
        aria-expanded={expanded}
      >
        <div>
          <p className="font-display text-sm font-bold">{station.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-fg-muted">
            {vehicles.length} Fahrzeug{vehicles.length === 1 ? '' : 'e'}
          </p>
        </div>
        <ChevronDown
          className={['h-4 w-4 text-fg-muted transition-transform', expanded ? 'rotate-180' : ''].join(' ')}
          aria-hidden
        />
      </button>
      {expanded && (
        <ul className="grid grid-cols-1 gap-1.5 border-t border-border-subtle p-3 md:grid-cols-2">
          {vehicles.map((v) => {
            const vSpec = VEHICLE_SPECS[v.type];
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => onSelectVehicle(v)}
                  className="flex w-full items-center gap-2.5 rounded-md border border-transparent bg-bg-elevated/50 px-2.5 py-2 text-left transition hover:border-border-subtle hover:bg-bg-elevated"
                >
                  <span
                    className={[
                      'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md font-display text-base font-bold',
                      STATUS_COLOR[v.status],
                    ].join(' ')}
                  >
                    {v.status}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold">{vSpec.label}</p>
                    <p className="truncate text-[10px] text-fg-muted">{vSpec.fullName}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 py-12 text-center">
      <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-border-strong text-fg-muted">
        <Truck className="h-5 w-5" aria-hidden />
      </span>
      <p className="font-display text-base font-semibold text-fg-secondary">
        Noch keine Fahrzeuge
      </p>
      <p className="mt-1 max-w-xs text-xs text-fg-muted">
        Baue zuerst eine Wache und kaufe dann Fahrzeuge.
      </p>
    </div>
  );
}
